/**
 * InvitacionMiembroTemplate — Fullscreen Mux video landing with DualCTA for VIP membership.
 *
 * Primary CTA "Ser Miembro":
 *   AR  → direct link to VITE_MP_CHECKOUT_VIP_AR (MercadoPago)
 *   REST → opens Stripe EmbeddedCheckout overlay (VIP plan, USD or EUR server-side)
 *
 * Secondary CTA "Canal gratis" → free Telegram URL from admin config.
 *
 * Video: simple_mux_overlay_playback_id from /api/premium-invite-config
 *   → VITE_PREMIUM_MUX_ES_VIP env fallback.
 *
 * Route: /invitacion-miembro
 */

import MuxVideoPlayerWithCTA from "@/components/MuxVideoPlayerWithCTA";
import MuxPosterBackdrop from "@/components/MuxPosterBackdrop";
import StripeCheckoutOverlay, {
  type CheckoutConfig,
} from "@/components/checkout/StripeCheckoutOverlay";
import { getPlansForCurrency } from "@/config/bioTemplatePremiumPlans";
import { useCurrentTemplate } from "@/contexts/CurrentTemplateContext";
import {
  applyInstagramBrowserCSS,
  isInstagramBrowser,
} from "@/lib/instagramBrowserDetection";
import { BRAND_SOURCE_KEY, BRAND_TELEGRAM_URL } from "@/lib/brandConfig";
import { buildStripeSuccessReturnUrl } from "@/lib/muchasGraciasSuccessUrl";
import { fetchPremiumInvitePublicConfig } from "@/lib/premiumInvitePublicConfig";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { trackTemplateCta, trackTemplateView } from "@/lib/templateCtaAnalytics";
import { cn } from "@/lib/utils";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

function mpTrim(...candidates: (string | undefined)[]): string {
  for (const c of candidates) {
    const t = typeof c === "string" ? c.trim() : "";
    if (t) return t;
  }
  return "";
}

const MP_CHECKOUT_VIP_AR = mpTrim(
  import.meta.env.VITE_MP_CHECKOUT_VIP_AR as string | undefined,
  import.meta.env.VITE_MP_VIP_LINK as string | undefined,
);

const FALLBACK_PLAYBACK_ID =
  (import.meta.env.VITE_PREMIUM_MUX_ES_VIP as string | undefined)?.trim() || "";

const FALLBACK_FREE_TELEGRAM_URL =
  (
    import.meta.env
      .VITE_PREMIUM_SIMPLE_FREE_TELEGRAM_URL as string | undefined
  )?.trim() ||
  (import.meta.env.VITE_TELEGRAM_FREE_GROUP_URL as string | undefined)?.trim() ||
  BRAND_TELEGRAM_URL?.trim() ||
  "";

export default function InvitacionMiembroTemplate() {
  const { country, language, isArgentina } = useTemplateSelection();
  const { setTemplateName } = useCurrentTemplate();
  const [searchParams] = useSearchParams();
  const inviteCode = (searchParams.get("code") || searchParams.get("ref") || "").trim() || undefined;

  const [playbackId, setPlaybackId] = useState(FALLBACK_PLAYBACK_ID);
  const [freeTelegramUrl, setFreeTelegramUrl] = useState(
    FALLBACK_FREE_TELEGRAM_URL,
  );
  /** Toggle maestro del "Elemento free-telegram" (admin). false = oculto en todo el sitio. */
  const [freeTelegramEnabled, setFreeTelegramEnabled] = useState(true);

  const [videoReady, setVideoReady] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [posterReady, setPosterReady] = useState(false);

  const [stripeCheckoutConfig, setStripeCheckoutConfig] =
    useState<CheckoutConfig | null>(null);

  useEffect(() => {
    setTemplateName(null);
    return () => setTemplateName(null);
  }, [setTemplateName]);

  useEffect(() => {
    fetchPremiumInvitePublicConfig()
      .then((cfg) => {
        if (!cfg) return;
        const id = cfg.simple_mux_overlay_playback_id?.trim();
        if (id) setPlaybackId(id);
        const ft = cfg.free_telegram_url?.trim();
        if (ft) setFreeTelegramUrl(ft);
        setFreeTelegramEnabled(cfg.free_telegram_enabled !== false);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const prev = {
      overflow: document.body.style.overflow,
      height: document.body.style.height,
      position: document.body.style.position,
      width: document.body.style.width,
    };
    document.body.style.overflow = "hidden";
    document.body.style.height = "100dvh";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    return () => {
      document.body.style.overflow = prev.overflow;
      document.body.style.height = prev.height;
      document.body.style.position = prev.position;
      document.body.style.width = prev.width;
    };
  }, []);

  useEffect(() => {
    if (!isInstagramBrowser()) return;
    applyInstagramBrowserCSS();
  }, []);

  useEffect(() => {
    trackTemplateView("invitacion_miembro", "InvitacionMiembroTemplate", {
      country,
      is_argentina: isArgentina === true,
      invite_code: inviteCode ?? null,
    });
  }, [country, isArgentina, inviteCode]);

  const handleMemberClick = useCallback(() => {
    trackTemplateCta({
      prefix: "invitacion_miembro",
      template: "InvitacionMiembroTemplate",
      action: "member",
      label: "Ser Miembro",
      plan: "vip",
      flowName: "invitacion_miembro",
      extra: {
        country,
        gateway: isArgentina ? "mercadopago" : "stripe_embedded",
        invite_code: inviteCode ?? null,
      },
    });

    if (isArgentina) {
      if (MP_CHECKOUT_VIP_AR) {
        window.location.href = MP_CHECKOUT_VIP_AR;
      } else {
        trackTemplateCta({
          prefix: "invitacion_miembro",
          template: "InvitacionMiembroTemplate",
          action: "checkout_link_missing",
          isClick: false,
          flowName: "invitacion_miembro",
          extra: { country, gateway: "mercadopago", invite_code: inviteCode ?? null },
        });
      }
      return;
    }

    // Non-AR: Stripe embedded checkout — internacional siempre habilitado
    // (ignora vipPlan.isDisabled / VITE_VIP_PLAN_CARD_ENABLED; coherente con DualCTAVideoLanding).
    const uiLanguage = language || "es";
    const { vipPlan } = getPlansForCurrency("USD", uiLanguage);

    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const successUrl = buildStripeSuccessReturnUrl(origin);
    const traceId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `invitacion_miembro_${Date.now()}`;

    trackTemplateCta({
      prefix: "invitacion_miembro",
      template: "InvitacionMiembroTemplate",
      action: "checkout_overlay_opened",
      isClick: false,
      flowName: "invitacion_miembro",
      extra: { country, gateway: "stripe_embedded", invite_code: inviteCode ?? null },
    });

    setStripeCheckoutConfig({
      amountCents: Math.round(vipPlan.price * 100),
      productName: vipPlan.productName,
      country: country || "US",
      returnUrl: successUrl,
      intervalCount: 1,
      inviteCode,
      metadata: {
        source: BRAND_SOURCE_KEY,
        flow: "invitacion_miembro",
        flow_trace_id: traceId,
        plan_tier: "vip",
        product_name: vipPlan.productName,
        billing_interval: "month",
        interval_count: "1",
        template: "InvitacionMiembroTemplate",
        checkout_started_at: new Date().toISOString(),
      },
    });
  }, [isArgentina, country, language, inviteCode]);

  const memberButtonDisabled =
    isArgentina && !MP_CHECKOUT_VIP_AR;

  return (
    <div
      className="relative w-full h-dvh min-h-dvh overflow-hidden bg-black instagram-browser-modal"
      style={{
        WebkitTransform: "translate3d(0,0,0)",
        touchAction: "none",
        overscrollBehavior: "none",
      }}
    >
      {playbackId && (
        <MuxPosterBackdrop
          playbackId={playbackId}
          onReady={() => setPosterReady(true)}
        />
      )}

      <MuxVideoPlayerWithCTA
        playbackId={playbackId || undefined}
        forceManualPlaybackId={!!playbackId}
        loop={false}
        autoPlay="any"
        skipWorkerTemplateCta={true}
        hideNativeCta={true}
        onReady={() => setVideoReady(true)}
        onPlay={() => {
          setVideoReady(true);
          setVideoPlaying(true);
        }}
        onEnded={() => setVideoEnded(true)}
      />

      {!videoPlaying && !videoEnded && (
        <div
          className="pointer-events-none absolute inset-0 z-[105] transition-opacity duration-500 ease-out"
          role="status"
          aria-live="polite"
          aria-busy={!videoReady}
        >
          {posterReady ? (
            <div
              className="absolute inset-x-0 bottom-0 flex justify-center"
              style={{
                paddingBottom:
                  "max(5.5rem, calc(env(safe-area-inset-bottom, 0px) + 5.5rem))",
              }}
            >
              <span className="inline-flex items-center gap-2 rounded-full bg-black/45 px-3.5 py-2 backdrop-blur-md ring-1 ring-white/10">
                <span className="relative h-3.5 w-3.5">
                  <span className="absolute inset-0 rounded-full border-2 border-white/15" />
                  <span className="absolute inset-0 rounded-full border-2 border-transparent border-t-amber-400 animate-spin" />
                </span>
                <span className="text-[11px] font-medium tracking-wide text-white/80">
                  Cargando…
                </span>
              </span>
            </div>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-5">
              <div className="absolute inset-0 bg-black/40" />
              <div className="relative h-12 w-12">
                <div className="absolute inset-0 rounded-full border-2 border-white/15" />
                <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-amber-400 animate-spin" />
              </div>
              <span className="relative text-[13px] font-medium uppercase tracking-[0.22em] text-white/70 animate-pulse">
                Cargando
              </span>
            </div>
          )}
        </div>
      )}

      {videoEnded && (
        <div
          className="absolute inset-0 z-[100] bg-black/90 transition-opacity duration-500"
          aria-hidden
        />
      )}

      {/* CTA overlay */}
      <div
        className="absolute inset-0 flex flex-col pointer-events-none"
        style={{ zIndex: 110 }}
      >
        {!videoEnded && (
          <div
            className="absolute bottom-0 left-0 right-0 h-40 [@media(min-height:700px)]:h-52"
            style={{
              background:
                "linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.20) 60%, transparent 100%)",
              pointerEvents: "none",
            }}
            aria-hidden
          />
        )}

        <div
          className={cn(
            "relative flex flex-1 justify-center px-5",
            videoEnded ? "items-center" : "items-end",
          )}
          style={
            videoEnded
              ? undefined
              : {
                  paddingBottom:
                    "max(1.5rem, env(safe-area-inset-bottom, 1.5rem))",
                }
          }
        >
          <div className="pointer-events-auto flex w-full max-w-sm flex-col items-stretch gap-3">
            {/* Primary CTA — VIP membership (amber pill, matching DualCTAVideoLanding style) */}
            <button
              type="button"
              disabled={memberButtonDisabled}
              onClick={handleMemberClick}
              className={cn(
                "group flex w-full items-center justify-center gap-1.5 rounded-full",
                "bg-amber-400/95 hover:bg-amber-300 active:bg-amber-500",
                "text-black text-sm font-semibold tracking-tight",
                "px-4 py-3",
                "shadow-[0_2px_14px_rgba(251,191,36,0.28)] hover:shadow-[0_2px_18px_rgba(251,191,36,0.38)]",
                "ring-1 ring-amber-300/40",
                "transition-all duration-150",
                "disabled:opacity-40 disabled:cursor-not-allowed",
              )}
              style={{
                touchAction: "manipulation",
                WebkitTapHighlightColor: "transparent",
                minHeight: 48,
              }}
            >
              <span className="truncate">Ser Miembro</span>
              <span
                className="text-[11px] opacity-70 transition-transform duration-150 group-hover:translate-x-0.5"
                aria-hidden
              >
                →
              </span>
            </button>

            {/* Secondary CTA — free Telegram (glass pill). Oculto si el toggle maestro está OFF
                o en Argentina (AR va directo a Stripe). */}
            {freeTelegramUrl && !isArgentina && freeTelegramEnabled && (
              <a
                href={freeTelegramUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  trackTemplateCta({
                    prefix: "invitacion_miembro",
                    template: "InvitacionMiembroTemplate",
                    action: "free_telegram",
                    label: "Canal gratis",
                    destination: freeTelegramUrl,
                    flowName: "invitacion_miembro",
                    extra: { country, invite_code: inviteCode ?? null },
                  })
                }
                className={cn(
                  "flex w-full items-center justify-center gap-1.5 rounded-full",
                  "bg-white/10 hover:bg-white/15 active:bg-white/20",
                  "backdrop-blur-md",
                  "text-white text-sm font-medium tracking-tight",
                  "px-4 py-2.5",
                  "ring-1 ring-white/20 hover:ring-white/30",
                  "transition-all duration-150",
                  "no-underline",
                )}
                style={{
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                  minHeight: 44,
                }}
              >
                <span className="truncate">Canal gratis</span>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Stripe embedded checkout overlay (non-AR) */}
      <StripeCheckoutOverlay
        open={!!stripeCheckoutConfig}
        config={stripeCheckoutConfig}
        onClose={() => {
          trackTemplateCta({
            prefix: "invitacion_miembro",
            template: "InvitacionMiembroTemplate",
            action: "checkout_overlay_closed",
            isClick: false,
            flowName: "invitacion_miembro",
            extra: { country, gateway: "stripe_embedded", invite_code: inviteCode ?? null },
          });
          setStripeCheckoutConfig(null);
        }}
        title="Membresía VIP"
      />
    </div>
  );
}
